import type { TimeAccuracy } from "@/lib/api/models/birth";
import { Button } from "@/ui/Button";
import { ClockIcon, PickerField } from "@/ui/PickerField";
import { Screen } from "@/ui/Screen";
import { Display, Tiny } from "@/ui/Text";
import { clockToMinutes, formatClock, minutesToClock } from "../time-format";

interface TimeStepProps {
  readonly minutes: number | null;
  readonly accuracy: TimeAccuracy;
  readonly onChangeMinutes: (minutes: number | null) => void;
  readonly onChangeAccuracy: (accuracy: TimeAccuracy) => void;
  readonly onContinue: () => void;
}

/**
 * The two ways out of giving an exact time. Exact is the default and the
 * thing we ask for; these are the honest alternatives, deliberately smaller.
 */
const FALLBACKS: ReadonlyArray<{ value: Exclude<TimeAccuracy, "exact">; label: string }> = [
  { value: "approximate", label: "Roughly, within an hour" },
  { value: "unknown", label: "I don't know" },
];

/**
 * Step 2 — birth time and how certain the person is about it. The single most
 * consequential input in the product: minutes move the ascendant. Capturing
 * certainty turns an invisible data-quality problem into a visible, honest one.
 */
export function TimeStep({ minutes, accuracy, onChangeMinutes, onChangeAccuracy, onContinue }: TimeStepProps) {
  const unknown = accuracy === "unknown";
  const answered = minutes !== null || unknown;
  return (
    <Screen
      middle
      progress={2 / 3}
      foot={<Button label="Continue" disabled={!answered} onClick={onContinue} />}
    >
      <Display>What time were you born?</Display>
      <div className="mt-8">
        <PickerField
          label="Time of birth"
          type="time"
          name="birth-time"
          value={minutes === null ? "" : minutesToClock(minutes)}
          prompt={unknown ? "We'll use midday" : accuracy === "approximate" ? "Tap to choose a rough time" : "Tap to choose the time"}
          display={minutes === null ? undefined : formatClock(minutes)}
          icon={<ClockIcon />}
          onChange={(e) => onChangeMinutes(clockToMinutes(e.target.value))}
        />
        <Tiny className="mt-2">A few minutes can change your rising sign, so the exact time matters.</Tiny>
      </div>
      <div className="mt-8">
        <Tiny>Not sure of the exact time?</Tiny>
        <div role="radiogroup" aria-label="How sure are you?" className="mt-2 flex flex-wrap gap-2">
          {FALLBACKS.map((option) => {
            const selected = accuracy === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={selected}
                // Tapping the chosen one again goes back to "I know it exactly".
                onClick={() => onChangeAccuracy(selected ? "exact" : option.value)}
                className={`min-h-11 border-[1.5px] px-4 text-[15px] transition-colors ${
                  selected ? "border-ink bg-ink text-paper" : "border-rule bg-paper text-ink hover:border-mid"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    </Screen>
  );
}
